/** The render tree both targets draw: a pure function of the documents and the held store. */

import type { Entity, LayerPlacement } from "@nebulize/sdk";

import { DEFAULT_SCOPE, type EntityIndex } from "../transport/snapshotStore";
import type { BoundValues } from "./bindings";
import { currentAttributeValue, formatText, hasTag, predicatesHold, resolveBindings } from "./bindings";
import type {
  DrawnKind,
  FlowLayout,
  LayoutBox,
  RegionMap,
  Repeat,
  SceneDocument,
  SceneNode,
  WireBackground,
} from "./contract";
import {
  BACKGROUND_KEY,
  BAR_VALUE_KEY,
  FRAME_KEY,
  isDrawnKind,
  LEFT_KEY,
  SUPPORTED_SCHEMA_VERSION,
  TOP_KEY,
} from "./contract";
import type { SceneReport } from "./report";
import { sceneReport } from "./report";

const DEFAULT_FLOW_DIRECTION = "column";
const DEFAULT_FLOW_GAP = 0;
const DEFAULT_FLOW_MAX_ITEMS = 16;
const DEFAULT_REPEAT_LIMIT = 32;

/** A box in the normalized space, absolute once the tree is built. */
export interface Box {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export const FULL_BOX: Box = { left: 0, top: 0, width: 1, height: 1 };

/** A point in the same normalized space as `Box`, absolute once the tree is built. */
export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A surface node's module, named by the activity that declared it. OVL-016. */
export interface SurfaceRef {
  readonly activityId: string;
  readonly name: string;
  /** The budget its declaration named, or undefined to leave the host on its own default. */
  readonly budgetMs: number | undefined;
  /**
   * The settings `[surfaces.<name>].settings` named, stamped onto the node by whoever assembled
   * the canvas. Empty when the declaration named none, which is every surface written before a
   * module could be configured at all. CAT-023.
   */
  readonly settings: SurfaceSettings;
}

/** What a surface module is configured with. Values, not references: substitution happened already. */
export type SurfaceSettings = Readonly<Record<string, unknown>>;

const NO_SURFACE_SETTINGS: SurfaceSettings = Object.freeze({});

export interface RenderNode {
  /** Unique across documents. The reconciler and the OVL-006 seam key on this. */
  readonly key: string;
  /** The activity whose document drew this. A backend sees a node and never the document. */
  readonly activityId: string;
  /** The canvas kind of the document that drew this, for the same reason `activityId` is here. */
  readonly canvasKind: string;
  readonly id: string;
  readonly kind: DrawnKind;
  readonly box: Box;
  /** Position among its siblings, as the document lists them. */
  readonly index: number;
  readonly style: Readonly<Record<string, string>>;
  readonly region: string | undefined;
  /** The authored string after substitution: what a `text` draws. Never a source to fetch. */
  readonly text: string;
  /** A `bar` magnitude in 0..1, undefined for every other kind. */
  readonly fill: number | undefined;
  /** The outline a `cells` instance draws, from its region's tiling. Undefined for every other. */
  readonly points: readonly Point[] | undefined;
  /** Which activity's declared surface draws this node. Undefined for every kind but `surface`. */
  readonly surface: SurfaceRef | undefined;
  /** What pressing this sends, resolved the same way the interact half resolves it. */
  readonly action: { readonly submission: string; readonly value: string } | undefined;
  readonly children: readonly RenderNode[];
}

export interface ResolvedCueBinding {
  readonly tagPattern: string;
  readonly presentation: string;
  readonly nodeKey: string;
}

export interface RenderedDocument {
  readonly key: string;
  readonly activityId: string;
  readonly canvasKind: string;
  readonly root: RenderNode;
  readonly cueBindings: readonly ResolvedCueBinding[];
}

export interface SceneOptions {
  /** The canvas kind this build draws. A document for another one is not drawn here. */
  readonly canvasKind: string;
  /** Region tilings by name, declared by the activity and resolved by the authority. */
  readonly regions?: ReadonlyMap<string, RegionMap>;
  /**
   * Where the customer put each activity on this canvas, and which one is on top. OVL-027.
   *
   * Arrives on the snapshot frame and is applied, never re-derived: the cloud clamped it on write
   * and every host draws the same layout. Absent, or absent for one document, leaves the
   * compositor's own default rule standing, which is what an unarranged space gets and is never
   * an error.
   */
  readonly arrangement?: Readonly<Record<string, LayerPlacement>>;
  readonly report?: SceneReport;
}

/** The default every unarranged document draws under: the whole canvas, on layer zero. */
export const DEFAULT_LAYER = 0;

/** One document's own box, from its placement or from the compositor's default. */
export function placedBox(placement: LayerPlacement | undefined): Box {
  if (placement === undefined) return FULL_BOX;
  return {
    left: placement.left ?? 0,
    top: placement.top ?? 0,
    width: placement.width ?? 1,
    height: placement.height ?? 1,
  };
}

/** Which placement, if any, this document answers to. */
function placementFor(
  document: SceneDocument,
  arrangement: Readonly<Record<string, LayerPlacement>> | undefined,
): LayerPlacement | undefined {
  if (arrangement === undefined) return undefined;
  const key = document.arrangement_key;
  return key == null ? undefined : arrangement[key];
}

interface BuildContext {
  readonly activityId: string;
  readonly canvasKind: string;
  readonly entities: EntityIndex;
  readonly regions: ReadonlyMap<string, RegionMap>;
  readonly report: SceneReport;
  readonly keys: Map<string, string>;
}

const NO_REGIONS: ReadonlyMap<string, RegionMap> = new Map<string, RegionMap>();

/** Region geometry by name, for the options a host passes to a build. */
export function regionsByName(
  regions: readonly RegionMap[],
): ReadonlyMap<string, RegionMap> {
  return new Map(regions.map((region) => [region.name, region]));
}

/** What tells one held document from another: the instance, falling back to the activity. */
function documentKey(document: SceneDocument): string {
  return `${document.added_activity_id ?? document.activity_id}/${document.canvas_kind}`;
}

/** A bound coordinate, or undefined when the key is unbound or bound to something that is not one.
 *
 * Substituted, never scaled: the attribute's value is already in the normalized space the box is in.
 */
function boundCoordinate(values: BoundValues, key: string): number | undefined {
  const bound = values.get(key);
  if (bound?.kind !== "number" || !Number.isFinite(bound.value)) return undefined;
  return bound.value;
}

function within(parent: Box, layout: LayoutBox | undefined, values?: BoundValues): Box {
  const width = layout?.width ?? 1;
  const height = layout?.height ?? 1;
  const left = (values && boundCoordinate(values, LEFT_KEY)) ?? layout?.left ?? 0;
  const top = (values && boundCoordinate(values, TOP_KEY)) ?? layout?.top ?? 0;

  return {
    left: parent.left + left * parent.width,
    top: parent.top + top * parent.height,
    width: parent.width * width,
    height: parent.height * height,
  };
}

/** The surface a node names, resolved against the activity whose document it came from. */
function surfaceRef(node: SceneNode, activityId: string): SurfaceRef | undefined {
  const name = node.surface;
  if (name === null || name === undefined || name.length === 0) return undefined;
  const budget = node.frame_budget_ms;
  const settings = node.surface_settings;
  return {
    activityId,
    name,
    budgetMs: typeof budget === "number" && budget > 0 ? budget : undefined,
    settings: settings ?? NO_SURFACE_SETTINGS,
  };
}

/** Whether a node places itself, so a `repeat` on it needs no enclosing flow and no region. */
function placesItself(node: SceneNode): boolean {
  return (node.bindings ?? []).some(
    (binding) => binding.key === LEFT_KEY || binding.key === TOP_KEY,
  );
}

/** The children a `frame` binding admits: the one it indexes, or all of them when it binds none. */
function framedChildren(
  children: readonly SceneNode[],
  values: BoundValues,
): readonly SceneNode[] {
  const bound = boundCoordinate(values, FRAME_KEY);
  if (bound === undefined) return children;

  const at = Math.floor(bound);
  const chosen = children[at];
  return chosen === undefined ? [] : [chosen];
}

/** The tiling a node draws on. Undefined when it names none, null when it names an unheld one. */
function regionOf(node: SceneNode, context: BuildContext): RegionMap | undefined | null {
  const name = node.region;
  if (name === null || name === undefined || name.length === 0) return undefined;

  const region = context.regions.get(name);
  if (region === undefined) {
    context.report.once(`region:${name}`, `node "${node.id}" names region "${name}", which the host does not hold`);
    return null;
  }
  return region;
}

/** What pressing a drawn node sends, or undefined for one nothing presses.
 *
 * An action inside a repeat may omit its value and take the item's id.
 */
function pressableAction(
  node: SceneNode,
  item: Entity | undefined,
): { submission: string; value: string } | undefined {
  const action = node.action;
  if (action === null || action === undefined) return undefined;
  const value = action.value ?? item?.id;
  if (value === undefined) return undefined;
  return { submission: action.submission, value };
}

/** How many cells a region has: what bounds a repeat placed on it, in place of `Repeat.limit`. */
function regionCells(region: RegionMap): number {
  return (region.cells ?? []).length;
}

/** The outline of the cell an entity id names, in `box`, or undefined for an id naming none.
 *
 * The wire carries a polygon as a flat run of x, y, so this reads it two at a time.
 */
function cellOutline(region: RegionMap, entityId: string, box: Box): Point[] | undefined {
  const cell = (region.cells ?? []).find((one) => one.id === entityId);
  if (cell === undefined) return undefined;

  const corners: Point[] = [];
  for (let at = 0; at + 1 < cell.points.length; at += 2) {
    const x = cell.points[at];
    const y = cell.points[at + 1];
    if (x === undefined || y === undefined) break;
    corners.push({ x: box.left + x * box.width, y: box.top + y * box.height });
  }
  return corners;
}

function barFill(values: BoundValues): number | undefined {
  const bound = values.get(BAR_VALUE_KEY);
  if (bound?.kind !== "number") return undefined;

  const { value, min, max } = bound;
  if (min === undefined || max === undefined) return undefined;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return undefined;

  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}

/** The entities `repeat` matches: sorted deterministically, capped, tag- and scope-filtered.
 *
 * Exported for the interact half, which expands the same `repeat` over the same entities and must
 * answer identically or one half draws a row the other does not. That is not a hypothetical: the
 * placement rule for a repeat is already encoded twice, in the sdk and here, and the graph carries
 * a caveat about teaching one and leaving the other refusing. A third copy was the version of this
 * that got written first, and the copy is what this export exists to delete.
 *
 * Exporting it costs the broadcast bundle nothing, because the function is already in it and
 * already called twice below. OVL-007 keeps control-layer code out of that bundle; it does not make
 * this file unreadable to a control layer, which loads both halves anyway.
 */
export function matchingEntities(repeat: Repeat, entities: EntityIndex, cap?: number): Entity[] {
  const scope = repeat.scope ?? DEFAULT_SCOPE;
  const facet = repeat.facet;
  const matches: Entity[] = [];

  for (const candidate of entities.values()) {
    if ((candidate.scope ?? DEFAULT_SCOPE) !== scope) continue;
    if (facet !== undefined && facet !== null && (candidate.facet ?? null) !== facet) continue;
    if (!hasTag(candidate, repeat.tag)) continue;
    matches.push(candidate);
  }

  const sortAttribute = repeat.sort_attribute;
  matches.sort((a, b) => {
    if (sortAttribute !== undefined && sortAttribute !== null) {
      const av = currentAttributeValue(a, sortAttribute);
      const bv = currentAttributeValue(b, sortAttribute);
      if (av !== undefined && bv !== undefined && av !== bv) return av - bv;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  return matches.slice(0, cap ?? repeat.limit ?? DEFAULT_REPEAT_LIMIT);
}

/** One box per instance along `flow`'s axis, each an equal share of it minus the gaps. */
function flowSlots(box: Box, flow: FlowLayout, count: number): Box[] {
  if (count <= 0) return [];

  const vertical = (flow.direction ?? DEFAULT_FLOW_DIRECTION) !== "row";
  const gap = flow.gap ?? DEFAULT_FLOW_GAP;
  const axis = vertical ? box.height : box.width;
  const itemSize = Math.max(0, axis - gap * (count - 1)) / count;

  const slots: Box[] = [];
  let cursor = vertical ? box.top : box.left;
  for (let i = 0; i < count; i += 1) {
    slots.push(
      vertical
        ? { left: box.left, top: cursor, width: box.width, height: itemSize }
        : { left: cursor, top: box.top, width: itemSize, height: box.height },
    );
    cursor += itemSize + gap;
  }
  return slots;
}

interface FlowInstance {
  readonly node: SceneNode;
  readonly item: Entity | undefined;
}

/** A flow's children, one per plain child and one per entity a `repeat` child matches. */
function flowInstances(
  nodes: readonly SceneNode[],
  entities: EntityIndex,
  outerItem: Entity | undefined,
): FlowInstance[] {
  const instances: FlowInstance[] = [];
  for (const node of nodes) {
    const repeat = node.repeat;
    if (repeat === null || repeat === undefined) {
      instances.push({ node, item: outerItem });
      continue;
    }
    for (const item of matchingEntities(repeat, entities)) instances.push({ node, item });
  }
  return instances;
}

function buildFlowChildren(
  node: SceneNode,
  box: Box,
  prefix: string,
  context: BuildContext,
  outerItem: Entity | undefined,
): readonly RenderNode[] {
  const flow = node.flow;
  if (flow === null || flow === undefined) return [];

  const all = flowInstances(node.children ?? [], context.entities, outerItem);
  const maxItems = flow.max_items ?? DEFAULT_FLOW_MAX_ITEMS;
  const visible = all.length > maxItems ? all.slice(0, maxItems) : all;
  if (all.length > maxItems) {
    context.report.once(
      `flow:${node.id}`,
      `node "${node.id}" flow holds ${String(all.length)} items, showing ${String(maxItems)}`,
    );
  }

  const slots = flowSlots(box, flow, visible.length);
  const built: RenderNode[] = [];

  visible.forEach((instance, slotIndex) => {
    const child = buildNode(
      instance.node,
      box,
      prefix,
      built.length,
      context,
      instance.item,
      slots[slotIndex],
    );
    if (child !== undefined) built.push(child);
  });

  return built;
}

function buildNode(
  node: SceneNode,
  parent: Box,
  prefix: string,
  index: number,
  context: BuildContext,
  item: Entity | undefined,
  presetFrame?: Box,
): RenderNode | undefined {
  if (!isDrawnKind(node.kind)) {
    context.report.once(`kind:${node.kind}`, `node kind "${node.kind}" is not drawn by this build`);
    return undefined;
  }

  const bindings = node.bindings ?? [];
  const values = resolveBindings(bindings, context.entities, item);
  if (!predicatesHold(bindings, values)) return undefined;

  const region = regionOf(node, context);
  if (region === null) return undefined;

  const frame = presetFrame ?? parent;
  const box = within(frame, node.layout, values);

  const points =
    region === undefined || item === undefined ? undefined : cellOutline(region, item.id, box);
  if (node.kind === "cells" && points === undefined) {
    context.report.once(
      `cells:${node.id}`,
      `node "${node.id}" is cells with no region tiling placing "${item?.id ?? "it"}", so it is not drawn`,
    );
    return undefined;
  }

  const fill = node.kind === "bar" ? barFill(values) : undefined;
  if (node.kind === "bar" && fill === undefined) {
    // Reported, because a bar is dropped for a reason an author cannot see from the outcome: the
    // binding key has to be BAR_VALUE_KEY exactly, and one named anything else leaves a board that
    // draws nothing and says nothing. Every other bail here reports; this one used to be silent.
    context.report.once(
      `bar:${node.id}`,
      `node "${node.id}" is a bar with no "${BAR_VALUE_KEY}" binding to a bounded number, so it is not drawn`,
    );
    return undefined;
  }

  const key = item === undefined ? `${prefix}/${node.id}` : `${prefix}/${node.id}/${item.id}`;

  const built: RenderNode = {
    key,
    activityId: context.activityId,
    canvasKind: context.canvasKind,
    id: node.id,
    kind: node.kind,
    box,
    index,
    style: { ...node.style },
    region: node.region ?? undefined,
    text: formatText(node.text, values),
    fill,
    points,
    surface: surfaceRef(node, context.activityId),
    action: pressableAction(node, item),
    children:
      node.flow === null || node.flow === undefined
        ? buildNodes(framedChildren(node.children ?? [], values), box, prefix, context, item)
        : buildFlowChildren(node, box, prefix, context, item),
  };

  context.keys.set(node.id, key);
  return built;
}

function buildNodes(
  nodes: readonly SceneNode[],
  parent: Box,
  prefix: string,
  context: BuildContext,
  item: Entity | undefined,
): readonly RenderNode[] {
  const built: RenderNode[] = [];

  for (const node of nodes) {
    const repeat = node.repeat;
    if (repeat !== null && repeat !== undefined) {
      // A flow gives instances a slot along an axis, a region gives them one in a plane, and a
      // bound position gives them one anywhere. With none of the three there is nowhere defined to
      // put them, so the repeat is dropped and reported.
      const region = regionOf(node, context);
      if (region === null) continue;
      if (region === undefined && !placesItself(node)) {
        context.report.once(
          `repeat:${node.id}`,
          `node "${node.id}" declares repeat with no enclosing flow, no region and no bound "${LEFT_KEY}" or "${TOP_KEY}", so it is not drawn`,
        );
        continue;
      }
      const cap = region === undefined ? undefined : regionCells(region);
      for (const one of matchingEntities(repeat, context.entities, cap)) {
        const child = buildNode(node, parent, prefix, built.length, context, one);
        if (child !== undefined) built.push(child);
      }
      continue;
    }
    const child = buildNode(node, parent, prefix, built.length, context, item);
    if (child !== undefined) built.push(child);
  }

  return built;
}

function resolveCueBindings(document: SceneDocument, keys: Map<string, string>): ResolvedCueBinding[] {
  const resolved: ResolvedCueBinding[] = [];

  for (const binding of document.cue_bindings ?? []) {
    const nodeKey = keys.get(binding.node_id);
    if (nodeKey === undefined) continue;
    resolved.push({
      tagPattern: binding.tag_pattern,
      presentation: binding.presentation,
      nodeKey,
    });
  }

  return resolved;
}

/** The layer beneath the authored nodes, when the document names one this build can draw.
 *
 * A colour draws. An image and a live video track do not yet: the first needs the asset resolver
 * and the second needs a video tier, and drawing a wrong one on a broadcast is worse than drawing
 * nothing.
 */
function buildBackground(
  background: WireBackground,
  prefix: string,
  report: SceneReport,
  box: Box,
  activityId: string,
  canvasKind: string,
): RenderNode | undefined {
  if (background === undefined || background === null) return undefined;

  if (background.layer !== "colour") {
    report.once(
      `background:${background.layer}`,
      `background layer "${background.layer}" is not drawn by this build`,
    );
    return undefined;
  }

  return {
    key: `${prefix}/${BACKGROUND_KEY}`,
    activityId,
    canvasKind,
    id: BACKGROUND_KEY,
    kind: "background",
    box,
    index: 0,
    style: { color: background.colour },
    region: undefined,
    text: "",
    fill: undefined,
    points: undefined,
    surface: undefined,
    action: undefined,
    children: [],
  };
}

function buildDocument(
  document: SceneDocument,
  index: number,
  entities: EntityIndex,
  options: SceneOptions,
  report: SceneReport,
): RenderedDocument | undefined {
  const version = document.schema_version ?? SUPPORTED_SCHEMA_VERSION;
  if (version !== SUPPORTED_SCHEMA_VERSION) {
    report.once(`version:${String(version)}`, `scene schema version ${String(version)} is not drawn by this build`);
    return undefined;
  }

  if (document.canvas_kind !== options.canvasKind) {
    report.once(
      `canvas:${document.canvas_kind}`,
      `document for canvas "${document.canvas_kind}" reached the "${options.canvasKind}" host`,
    );
    return undefined;
  }

  const placement = placementFor(document, options.arrangement);
  if (placement?.hidden === true) return undefined;

  const key = documentKey(document);
  const box = placedBox(placement);
  const context: BuildContext = {
    activityId: document.activity_id,
    canvasKind: document.canvas_kind,
    entities,
    regions: options.regions ?? NO_REGIONS,
    report,
    keys: new Map<string, string>(),
  };

  const background = buildBackground(
    document.background,
    key,
    report,
    box,
    document.activity_id,
    document.canvas_kind,
  );
  const authored = buildNodes(document.nodes ?? [], box, key, context, undefined);
  const children =
    background === undefined
      ? authored
      : [background, ...authored.map((node, index) => ({ ...node, index: index + 1 }))];

  return {
    key,
    activityId: document.activity_id,
    canvasKind: document.canvas_kind,
    root: {
      key,
      activityId: document.activity_id,
      canvasKind: document.canvas_kind,
      id: key,
      kind: "group",
      box,
      index,
      style: {},
      region: undefined,
      text: "",
      fill: undefined,
      points: undefined,
      surface: undefined,
      action: undefined,
      children,
    },
    cueBindings: resolveCueBindings(document, context.keys),
  };
}

/** Build every document the host holds. A document that cannot be drawn contributes nothing. */
export function buildScene(
  documents: readonly SceneDocument[],
  entities: EntityIndex,
  options: SceneOptions,
): readonly RenderedDocument[] {
  const report = options.report ?? sceneReport;
  const built: RenderedDocument[] = [];

  for (const document of ordered(documents, options.arrangement)) {
    const rendered = buildDocument(document, built.length, entities, options, report);
    if (rendered !== undefined) built.push(rendered);
  }

  return built;
}

/**
 * The documents in the order the compositor draws them: layer first, arrival order behind it.
 *
 * Total over every document, arranged or not. An unarranged one sits on `DEFAULT_LAYER`, which is
 * what makes "bring this one to the front and leave the rest alone" behave the way the person
 * dragging it expects rather than sending every untouched activity to the back.
 *
 * Stable within a layer, and the tiebreak is arrival order rather than a name, because arrival
 * order is the rule this feature moves the decision away from without replacing what it did.
 */
export function ordered(
  documents: readonly SceneDocument[],
  arrangement: Readonly<Record<string, LayerPlacement>> | undefined,
): readonly SceneDocument[] {
  if (arrangement === undefined) return documents;
  return documents
    .map((document, arrival) => ({
      document,
      arrival,
      layer: placementFor(document, arrangement)?.layer ?? DEFAULT_LAYER,
    }))
    .sort((left, right) => left.layer - right.layer || left.arrival - right.arrival)
    .map((one) => one.document);
}
